// src/redux/companySession/companySessionSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createCompanySessionAPI,
  getCompanySessionsAPI,
  getCompanySessionDetailAPI,
  getParticularSessionDetailsAPI,
  enableToolAccessAPI,
  enableSupportDocumentAPI,
  enableAssignmentAPI,
  enableCompanySessionAPI,
} from "./companySessionApi";

// ✅ Create Company Session
export const createCompanySession = createAsyncThunk(
  "companySession/createCompanySession",
  async ({ companyId, sessionData }, { rejectWithValue }) => {
    try {
      const response = await createCompanySessionAPI(companyId, sessionData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create company session");
    }
  }
);

// ✅ Get Company Sessions
export const getCompanySessions = createAsyncThunk(
  "companySession/getCompanySessions",
  async ({ company_id, page = 1, limit = 10, search = "", from_date = null, to_date = null, session_type } = {}, { rejectWithValue }) => {
    try {
      const response = await getCompanySessionsAPI({ company_id, page, limit, search, from_date, to_date, session_type });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch company sessions");
    }
  }
);

// ✅ Get Company Session Detail (GET method)
export const getCompanySessionDetail = createAsyncThunk(
  "companySession/getCompanySessionDetail",
  async ({ companyId, sessionId }, { rejectWithValue }) => {
    try {
      const response = await getCompanySessionDetailAPI(companyId, sessionId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch session details");
    }
  }
);

// ✅ Get Particular Session Details (POST method)
export const getParticularSessionDetails = createAsyncThunk(
  "companySession/getParticularSessionDetails",
  async ({ companyId, companySessionId }, { rejectWithValue }) => {
    try {
      const response = await getParticularSessionDetailsAPI(companyId, companySessionId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch particular session details");
    }
  }
);

// ✅ Enable Tool Access
export const enableToolAccess = createAsyncThunk(
  "companySession/enableToolAccess",
  async ({ companyId, toolData }, { rejectWithValue }) => {
    try {
      const response = await enableToolAccessAPI(companyId, toolData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to enable tool access");
    }
  }
);

// ✅ Enable Support Document
export const enableSupportDocument = createAsyncThunk(
  "companySession/enableSupportDocument",
  async ({ companyId, documentData }, { rejectWithValue }) => {
    try {
      const response = await enableSupportDocumentAPI(companyId, documentData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to enable support document");
    }
  }
);

// ✅ Enable Assignment
export const enableAssignment = createAsyncThunk(
  "companySession/enableAssignment",
  async ({ companyId, assignmentData }, { rejectWithValue }) => {
    try {
      const response = await enableAssignmentAPI(companyId, assignmentData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to send assignment");
    }
  }
);

// ✅ Enable / Disable Company Session (assign_status)
export const enableCompanySession = createAsyncThunk(
  "companySession/enableCompanySession",
  async ({ companyId, body }, { rejectWithValue }) => {
    try {
      const response = await enableCompanySessionAPI(companyId, body);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update company session");
    }
  }
);

// ---------- Initial State ----------
const initialState = {
  createCompanySession: { data: null, loading: false, error: null },
  getCompanySessions: { data: [], total: 0, loading: false, error: null },
  getCompanySessionDetail: { data: null, loading: false, error: null },
  getParticularSessionDetails: { data: null, loading: false, error: null },
  enableToolAccess: { data: null, loading: false, error: null },
  enableSupportDocument: { data: null, loading: false, error: null },
  enableAssignment: { data: null, loading: false, error: null },
  enableCompanySession: { data: null, loading: false, error: null },
};

// ---------- Slice ----------
const companySessionSlice = createSlice({
  name: "companySession",
  initialState,
  reducers: {
    clearCreateCompanySession: (state) => {
      state.createCompanySession.data = null;
      state.createCompanySession.error = null;
    },
    clearCompanySessions: (state) => {
      state.getCompanySessions.data = [];
      state.getCompanySessions.error = null;
      state.getCompanySessions.total = 0;
    },
    clearCompanySessionDetail: (state) => {
      state.getCompanySessionDetail.data = null;
      state.getCompanySessionDetail.error = null;
    },
    clearParticularSessionDetails: (state) => {
      state.getParticularSessionDetails.data = null;
      state.getParticularSessionDetails.error = null;
    },
    clearEnableToolAccess: (state) => {
      state.enableToolAccess.data = null;
      state.enableToolAccess.error = null;
    },
    clearEnableSupportDocument: (state) => {
      state.enableSupportDocument.data = null;
      state.enableSupportDocument.error = null;
    },
    clearEnableAssignment: (state) => {
      state.enableAssignment.data = null;
      state.enableAssignment.error = null;
    },
  },
  extraReducers: (builder) => {
    // ---------- Create Company Session ----------
    builder
      .addCase(createCompanySession.pending, (state) => {
        state.createCompanySession.loading = true;
        state.createCompanySession.error = null;
      })
      .addCase(createCompanySession.fulfilled, (state, action) => {
        state.createCompanySession.loading = false;
        state.createCompanySession.data = action.payload?.data || action.payload;
      })
      .addCase(createCompanySession.rejected, (state, action) => {
        state.createCompanySession.loading = false;
        state.createCompanySession.error = action.payload;
      });

    // ---------- Get Company Sessions ----------
     builder
      .addCase(getCompanySessions.pending, (state) => {
        state.getCompanySessions.loading = true;
        state.getCompanySessions.error = null;
      })
      .addCase(getCompanySessions.fulfilled, (state, action) => {
        state.getCompanySessions.loading = false;
        
        // Data is at root level in your JSON
        state.getCompanySessions.data = action.payload?.data || [];
        
        // ✅ FIX: Total is inside the pagination object in your JSON
        state.getCompanySessions.total = action.payload?.pagination?.total || 0; 
      })
      .addCase(getCompanySessions.rejected, (state, action) => {
        state.getCompanySessions.loading = false;
        state.getCompanySessions.error = action.payload;
      });




    // ---------- Get Company Session Detail ----------
    builder
      .addCase(getCompanySessionDetail.pending, (state) => {
        state.getCompanySessionDetail.loading = true;
        state.getCompanySessionDetail.error = null;
      })
      .addCase(getCompanySessionDetail.fulfilled, (state, action) => {
        state.getCompanySessionDetail.loading = false;
        state.getCompanySessionDetail.data = action.payload?.data || action.payload;
      })
      .addCase(getCompanySessionDetail.rejected, (state, action) => {
        state.getCompanySessionDetail.loading = false;
        state.getCompanySessionDetail.error = action.payload;
      });

    // ---------- Get Particular Session Details ----------
    builder
      .addCase(getParticularSessionDetails.pending, (state) => {
        state.getParticularSessionDetails.loading = true;
        state.getParticularSessionDetails.error = null;
      })
      .addCase(getParticularSessionDetails.fulfilled, (state, action) => {
        state.getParticularSessionDetails.loading = false;
        state.getParticularSessionDetails.data = action.payload?.data || action.payload;
      })
      .addCase(getParticularSessionDetails.rejected, (state, action) => {
        state.getParticularSessionDetails.loading = false;
        state.getParticularSessionDetails.error = action.payload;
      });

    // ---------- Enable Tool Access ----------
    builder
      .addCase(enableToolAccess.pending, (state) => {
        state.enableToolAccess.loading = true;
        state.enableToolAccess.error = null;
      })
      .addCase(enableToolAccess.fulfilled, (state, action) => {
        state.enableToolAccess.loading = false;
        state.enableToolAccess.data = action.payload?.data || action.payload;
      })
      .addCase(enableToolAccess.rejected, (state, action) => {
        state.enableToolAccess.loading = false;
        state.enableToolAccess.error = action.payload;
      });

    // ---------- Enable Support Document ----------
    builder
      .addCase(enableSupportDocument.pending, (state) => {
        state.enableSupportDocument.loading = true;
        state.enableSupportDocument.error = null;
      })
      .addCase(enableSupportDocument.fulfilled, (state, action) => {
        state.enableSupportDocument.loading = false;
        state.enableSupportDocument.data = action.payload?.data || action.payload;
      })
      .addCase(enableSupportDocument.rejected, (state, action) => {
        state.enableSupportDocument.loading = false;
        state.enableSupportDocument.error = action.payload;
      });

    // ---------- Enable Assignment ----------
    builder
      .addCase(enableAssignment.pending, (state) => {
        state.enableAssignment.loading = true;
        state.enableAssignment.error = null;
      })
      .addCase(enableAssignment.fulfilled, (state, action) => {
        state.enableAssignment.loading = false;
        state.enableAssignment.data = action.payload?.data || action.payload;
      })
      .addCase(enableAssignment.rejected, (state, action) => {
        state.enableAssignment.loading = false;
        state.enableAssignment.error = action.payload;
      });

    // ---------- Enable Company Session (assign_status) ----------
    builder
      .addCase(enableCompanySession.pending, (state) => {
        state.enableCompanySession.loading = true;
        state.enableCompanySession.error = null;
      })
      .addCase(enableCompanySession.fulfilled, (state, action) => {
        state.enableCompanySession.loading = false;
        state.enableCompanySession.data = action.payload?.data || action.payload;
      })
      .addCase(enableCompanySession.rejected, (state, action) => {
        state.enableCompanySession.loading = false;
        state.enableCompanySession.error = action.payload;
      });
  },
});

export const {
  clearCreateCompanySession,
  clearCompanySessions,
  clearCompanySessionDetail,
  clearParticularSessionDetails,
  clearEnableToolAccess,
  clearEnableSupportDocument,
  clearEnableAssignment,
} = companySessionSlice.actions;

export default companySessionSlice.reducer;